// Pure localStorage wrappers + auto-open bookkeeping for MyWashPromoV2.
//
// Which wash (if any) personalizes the ad is no longer resolved here, or
// anywhere in the browser: the backend (GET /mywash-promo/me) already
// knows the logged-in user's own companies/business units and returns
// only their wash, already filtered - see mywashPromoService.js. This file
// only keeps the auto-open frequency rules, written as plain functions
// that take their state explicitly (including "now") so tests can
// exercise the rules without mocking Date or waiting on real timers. The
// storage read/write functions are the only impure bits, kept thin.

const AUTO_OPEN_LIMIT = 2;
const AUTO_OPEN_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const DEFAULT_STATE = Object.freeze({ autoCount: 0, lastAutoAt: 0, never: false });

const storageKey = (userId) => `mywash_promo_v2_${userId}`;

// ---------------------------------------------------------------------------
// localStorage read/write (impure, wrapped so a broken storage never throws)
// ---------------------------------------------------------------------------

export const readPromoState = (userId) => {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { ...DEFAULT_STATE };
    return {
      autoCount: Number(parsed.autoCount) || 0,
      lastAutoAt: Number(parsed.lastAutoAt) || 0,
      never: Boolean(parsed.never),
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
};

export const writePromoState = (userId, state) => {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    // localStorage unavailable (private mode, quota, disabled) - ignore
  }
};

// ---------------------------------------------------------------------------
// Pure auto-open rules
// ---------------------------------------------------------------------------

// May the dialog auto-open right now, given this state and the current time?
export const canAutoOpen = (state, now) => {
  if (!state || state.never) return false;
  if ((state.autoCount || 0) >= AUTO_OPEN_LIMIT) return false;
  if (!state.lastAutoAt) return true;
  return now - state.lastAutoAt >= AUTO_OPEN_COOLDOWN_MS;
};

// Next state after recording an automatic open.
export const withAutoOpenRecorded = (state, now) => ({
  ...state,
  autoCount: (state?.autoCount || 0) + 1,
  lastAutoAt: now,
});

// Next state after "never show again".
export const withNeverRecorded = (state) => ({
  ...state,
  never: true,
});

// Convenience wrappers used by the component (read -> transform -> write).
export const shouldAutoOpen = (userId, now) => canAutoOpen(readPromoState(userId), now);

export const recordAutoOpen = (userId, now) => {
  const next = withAutoOpenRecorded(readPromoState(userId), now);
  writePromoState(userId, next);
  return next;
};

export const recordNever = (userId) => {
  const next = withNeverRecorded(readPromoState(userId));
  writePromoState(userId, next);
  return next;
};
